import { Route, Switch, useRouteMatch } from "react-router-dom"
import {
	refreshAraAccessToken,
	storeRefreshCode,
	storeSignature,
	NotFound,
	AccessDenied,
	AccessExpired,
	lazyWithForcedRefresh,
	UnauthenticatedRoute,
	AuthenticatedRoute,
	LoaderFallback,
	ErrorBoundary,
	IframePopOutButton,
	useQuery,
} from "shared"
import { OAuthCallback } from "shared/src/components/Integrations/OAuthCallback"
import { Suspense, useMemo } from "react"

const PresentCVDisplay = lazyWithForcedRefresh(
	() => import(/* webpackChunkName: "PresentCVDisplay" */ "./Present/PresentCVDisplay")
)
const DigitalCVDisplay = lazyWithForcedRefresh(
	() => import(/* webpackChunkName: "DigitalCVDisplay" */ "./DigitalCVDisplay/DigitalCVDisplay")
)
const PlainCVDisplay = lazyWithForcedRefresh(
	() => import(/* webpackChunkName: "PlainCVDisplay" */ "./PlainCVDisplay/PlainCVDisplay")
)
const LinkLoad = lazyWithForcedRefresh(() => import(/* webpackChunkName: "LinkLoad" */ "./LinkLoad/LinkLoad"))
const TemplateDisplay = lazyWithForcedRefresh(
	() => import(/* webpackChunkName: "TemplateDisplay" */ "./Template/TemplateDisplay/TemplateDisplay")
)

const BullhornOldToNewPatch = lazyWithForcedRefresh(
	() =>
		import(
			/* webpackChunkName: "BullhornOldToNewPatch" */ "shared/src/components/BullhornIntegration/BullhornOldToNewPatch"
		)
)
const ApplyDefaultTemplate = lazyWithForcedRefresh(
	() => import(/* webpackChunkName: "ApplyDefaultTemplate" */ "./ApplyDefaultTemplate/ApplyDefaultTemplate")
)

const PublicAddJob = lazyWithForcedRefresh(() => import(/* webpackChunkName: "PublicAddJob" */ "./Jobs/PublicAddJob"))
const EntryPoint = lazyWithForcedRefresh(() => import(/* webpackChunkName: "EntryPoint" */ "./EntryPoint/EntryPoint"))
const CustomEntryPoint = lazyWithForcedRefresh(
	() => import(/* webpackChunkName: "CustomEntryPoint" */ "./EntryPoint/CustomEntryPoint")
)
const IntegrationPage = lazyWithForcedRefresh(
	() => import(/* webpackChunkName: "IntegrationPage" */ "./Integrations/IntegrationPage")
)
const IntegrationSetupPage = lazyWithForcedRefresh(
	() => import(/* webpackChunkName: "IntegrationSetupPage" */ "./Integrations/IntegrationSetupPage")
)
const Cookies = lazyWithForcedRefresh(() => import(/* webpackChunkName: "Cookies" */ "./Cookies/Cookies"))

const refreshTokenPath = "/candidate/link/:shortId"

/**
 * Refreshes the access token and refresh token when the user navigates to the refresh token path
 * Needed to make sure that a refresh token is stored in the browser before login actions take place,
 * as race conditions can break the login if done in the linkload page
 */
export function useRefreshToken() {
	const match = useRouteMatch(refreshTokenPath)
	useMemo(async () => {
		if (match?.isExact) {
			const shortId = match.params?.shortId
			// save refresh token with short expiry ahead of proper checking to avoid access denied flow being triggered during wait
			storeRefreshCode(shortId, 3000) //js uses milliseconds
			const { refreshExpiry, accessToken } = await refreshAraAccessToken(shortId)
			// save refresh token with correct expiry
			storeRefreshCode(shortId, refreshExpiry * 1000) //js uses milliseconds
			// save access token
			storeSignature(accessToken)
		}
	}, [match?.isExact, match?.params?.shortId])
}

export default function Routes({ mapHomeToCandidatePage = false }) {
	const query = useQuery()
	const allowChatQSParam = query.get("allow_chat")
	const allowChat: boolean = allowChatQSParam === "true" || allowChatQSParam === "1"

	window.Intercom?.("update", {
		hide_default_launcher: !allowChat, // default no chat in iframe/single page
	})

	return (
		<ErrorBoundary>
			<Suspense fallback={<LoaderFallback />}>
				<Switch>
					<AuthenticatedRoute path="/singlepageview/application/:appId">
						<PresentCVDisplay />
					</AuthenticatedRoute>
					<AuthenticatedRoute path="/singlepageview/digitalcv/:appId">
						<DigitalCVDisplay />
					</AuthenticatedRoute>
					<AuthenticatedRoute path="/candidate/application/:appId">
						<PresentCVDisplay />
					</AuthenticatedRoute>
					<Route path={refreshTokenPath}>
						<LinkLoad />
					</Route>
					<AuthenticatedRoute assumeSignature={true} path="/candidate/digitalcv/:appId">
						<DigitalCVDisplay />
					</AuthenticatedRoute>
					<AuthenticatedRoute path="/singlepageview/addjob">
						<PublicAddJob />
					</AuthenticatedRoute>
					<AuthenticatedRoute path="/singlepageview/integration/setup">
						<IntegrationSetupPage />
					</AuthenticatedRoute>
					<AuthenticatedRoute path="/singlepageview/integration">
						<IntegrationPage />
					</AuthenticatedRoute>
					<AuthenticatedRoute path="/singlepageview/template/:templateId">
						<TemplateDisplay />
					</AuthenticatedRoute>
					<AuthenticatedRoute path="/apply">
						<ApplyDefaultTemplate />
					</AuthenticatedRoute>
					<Route path="/oauthcallback">
						<OAuthCallback />
					</Route>
					<Route path="/entrypoint/custom/:platform/:endPoint">
						<CustomEntryPoint />
					</Route>
					<Route path="/entrypoint/:endPoint">
						<EntryPoint />
					</Route>
					<Route path="/bullhorn/:apiKey/:secret">
						<BullhornOldToNewPatch />
					</Route>
					<AuthenticatedRoute assumeSignature={true} path="/singlepageview/plain/:appId">
						<PlainCVDisplay />
					</AuthenticatedRoute>
					<AuthenticatedRoute assumeSignature={true} path="/singlepageview/plaintemplate/:templateId">
						<PlainCVDisplay />
					</AuthenticatedRoute>
					<UnauthenticatedRoute path="/accessexpired">
						<AccessExpired />
					</UnauthenticatedRoute>
					<Route path="/cookies">
						<Cookies />
					</Route>
					<Route path="/accessDenied">
						<AccessDenied />
					</Route>
					{mapHomeToCandidatePage && (
						<Route path={"/:shortId"}>
							{/* must be last route with path as the variable will take any value{" "} */}
							{/* This is the home page when accessed from candidates.live equivalent to /candidate/link/:shortId */}
							<LinkLoad />
						</Route>
					)}
					<Route>
						<NotFound />
					</Route>
				</Switch>
			</Suspense>
			<IframePopOutButton />
		</ErrorBoundary>
	)
}
